import React, { useContext, useState } from 'react';
import { AlertBanner, Button, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import {
    disarmAutoTrain,
    isAutoTrainArmed,
    listActiveDataFlowsForUsecase,
} from '@/features/trainings/utils/autoTrain';

/**
 * "Train automatically when data is ready" banner on the Train step / Retrain
 * modal. Visible only while Generate-with-AI / Retrieval Agent flows or
 * dataset/benchmark uploads are running for the task (or when auto-train is
 * already armed): offers arming, and once armed shows the indicative
 * scheduled state with a Cancel action.
 */
const AutoTrainScheduleSection = ({ usecase, onArm, arming = false, readOnly = false }) => {
    const { progressStatus, updateUsecase } = useContext(GlobalContext);
    const [disarming, setDisarming] = useState(false);

    if (!usecase) return null;

    const armed = isAutoTrainArmed(usecase);
    const activeFlows = listActiveDataFlowsForUsecase(progressStatus, usecase._id);
    if (!armed && activeFlows.length === 0) return null;

    const handleDisarm = () => {
        if (disarming) return;
        setDisarming(true);
        disarmAutoTrain({
            usecase,
            onSuccess: () => {
                updateUsecase?.(usecase._id, { auto_train_pending: null });
                sparkToast.success('Automatic training canceled');
                setDisarming(false);
            },
            onError: (message) => {
                sparkToast.error(message);
                setDisarming(false);
            },
        });
    };

    if (armed) {
        return (
            <AlertBanner
                className="auto-train-schedule-banner fs-unmask"
                feedback="success"
                showCloseButton={false}
                text="Training will start automatically when all data flows finish"
                action={
                    readOnly ? null : (
                        <Button
                            variant="link"
                            size="medium"
                            type="button"
                            disabled={disarming}
                            disableWhenReadOnly
                            onClick={handleDisarm}
                        >
                            {disarming ? 'Canceling…' : 'Cancel'}
                        </Button>
                    )
                }
            />
        );
    }

    if (readOnly) return null;

    const flowsLabel =
        activeFlows.length === 1
            ? 'A data flow is still running'
            : `${activeFlows.length} data flows are still running`;

    return (
        <AlertBanner
            className="auto-train-schedule-banner fs-unmask"
            feedback="info"
            showCloseButton={false}
            text={`${flowsLabel} - you don't have to wait for it to finish.`}
            action={
                <Button
                    variant="link"
                    size="medium"
                    type="button"
                    disabled={arming}
                    disableWhenReadOnly
                    onClick={() => onArm?.()}
                >
                    {arming ? 'Scheduling…' : 'Train automatically when data is ready'}
                </Button>
            }
        />
    );
};

export default AutoTrainScheduleSection;
